import {computed, DestroyRef, effect, inject, Injectable, OnDestroy, signal, untracked} from "@angular/core"
import {takeUntilDestroyed} from "@angular/core/rxjs-interop"
import {TraverseType, WebSocketMessaging} from "@app/common/helpers/websocket/websocket-messaging"
import {z} from "zod/v4-mini"
import type {CmLogger} from "@cm/log"
import {CM_LOGGER_SCENE} from "@common/services/logging"
import {SceneManagerService} from "@app/template-editor/services/scene-manager.service"
import {SceneManagerCacheService} from "@app/template-editor/services/scene-manager-cache.service"
import {
    AreaLight,
    Camera,
    DataObjectReference,
    defaultsForToneMapping,
    isMeshLikeNode,
    LightPortal,
    MaterialAssignment,
    MaterialAssignments,
    MeshCurve,
    MeshDecal,
    HdriLight,
    type MeshLikeNode,
    nodesAlongCollections,
    TemplateNode,
    TemplateNodes,
    PointLight,
    PostProcessRender,
    Render,
    SceneNodes,
    StoredMesh,
    SunLight,
    PositionControlPoint,
    RawPolyMeshData,
    getRootTemplateNodeForObjectPath,
    getTemplateNodeForObjectPath,
    isNamedNode,
    targetFromTransform,
    blenderMeshSource,
    rebuildDecal,
    sharedMaterialReferences,
    fromBlenderCoordinates,
    fromBlenderLength,
    fromBlenderTransform,
    toBlenderCoordinates,
    toBlenderTransform,
    blenderMaterialSlots,
    blenderCamera,
    blenderCollections,
    blenderCurveSplines,
    blenderLight,
    densifyControlPoints,
    removeDuplicates,
    simplifyControlPoints,
    type BlenderCurveObject,
    type BlenderDecal,
    type BlenderDecalImage,
    type BlenderHdri,
    type BlenderImportObjectsMessage,
    type BlenderMaterial,
    type BlenderMeshObject,
    type BlenderPostProcess,
    type BlenderStatusMessage,
} from "@cm/template-nodes"
import {Matrix4, Vector3} from "@cm/math"
import {combineUvOffsets, materialGraphChanges, transformOffsetUvs, uvOffsetsBetween} from "@cm/material-nodes"
import type {IMaterialData, MaterialGraph} from "@cm/material-nodes"
import {convertRawPolyMeshToDracoAndPLY, evaluateRawPolyMesh, MeshConversionOptions} from "@app/template-editor/helpers/mesh-processing"
import {firstValueFrom, timer} from "rxjs"
import {UploadGqlService} from "@app/common/services/upload/upload.gql.service"
import {DataObjectType} from "@generated"
import {closestCurvePointsToObject, toThreeMatrix} from "@app/template-editor/helpers/three-utils"
import {projectMeshCurveOntoMesh} from "@app/template-editor/helpers/three-mesh-curve-control"
import {ThreeSceneManagerService} from "@app/template-editor/services/three-scene-manager.service"
import {
    DataObjectForBlenderGQL,
    ExchangeNodesAndConnectionsForBlenderGQL,
    HdriForBlenderGQL,
    GetMaterialLegacyIdForBlenderConnectorGQL,
    MaterialRevisionForBlenderGQL,
    TextureRevisionForBlenderGQL,
    TextureSetRevisionForBlenderGQL,
} from "@app/template-editor/services/blender-connector.service.generated"
import {fetchThrowingErrors} from "@app/common/helpers/api/fetch"
import {BackgroundOperationService} from "@app/platform/services/background-operation/background-operation.service"
import type {BackgroundOperationItem} from "@app/common/models/background-operation/background-operation-item"
import {NotificationsService} from "@common/services/notifications/notifications.service"
import filenamify from "filenamify/browser"
import {environment} from "@environment"

const vector3 = z.object({x: z.number(), y: z.number(), z: z.number()})

const inBatch = {
    collections: z.optional(z.array(z.string())),
    batch: z.optional(z.object({id: z.string(), index: z.number()})),
}

export const addLightSchema = z.object({
    type: z.literal("add_light"),
    name: z.string(),
    ...inBatch,
    location: z.object({
        x: z.number(),
        y: z.number(),
        z: z.number(),
    }),
    rotation: z.object({
        x: z.number(),
        y: z.number(),
        z: z.number(),
    }),
    color: z.object({
        r: z.number(),
        g: z.number(),
        b: z.number(),
    }),
    width: z.number(),
    height: z.number(),
    spread: z.number(),
    power: z.number(),
    visible: z.boolean(),
})

export const addPointLightSchema = z.object({
    type: z.literal("add_point_light"),
    name: z.string(),
    ...inBatch,
    location: z.object({
        x: z.number(),
        y: z.number(),
        z: z.number(),
    }),
    rotation: z.object({
        x: z.number(),
        y: z.number(),
        z: z.number(),
    }),
    color: z.object({
        r: z.number(),
        g: z.number(),
        b: z.number(),
    }),
    radius: z.number(),
    power: z.number(),
    visible: z.boolean(),
})

export const addSunLightSchema = z.object({
    type: z.literal("add_sun_light"),
    name: z.string(),
    ...inBatch,
    location: z.object({
        x: z.number(),
        y: z.number(),
        z: z.number(),
    }),
    rotation: z.object({
        x: z.number(),
        y: z.number(),
        z: z.number(),
    }),
    color: z.object({
        r: z.number(),
        g: z.number(),
        b: z.number(),
    }),
    angle: z.number(),
    power: z.number(),
    visible: z.boolean(),
})

export const addLightPortalSchema = z.object({
    type: z.literal("add_light_portal"),
    name: z.string(),
    ...inBatch,
    location: z.object({
        x: z.number(),
        y: z.number(),
        z: z.number(),
    }),
    rotation: z.object({
        x: z.number(),
        y: z.number(),
        z: z.number(),
    }),
    width: z.number(),
    height: z.number(),
})

// the tone mappings a Blender view transform has an exact counterpart for: Standard and Khronos PBR Neutral
const blenderToneMappingSchema = z.enum(["linear", "pbr-neutral"])

export const addCameraSchema = z.object({
    type: z.literal("add_camera"),
    name: z.string(),
    ...inBatch,
    location: z.object({
        x: z.number(),
        y: z.number(),
        z: z.number(),
    }),
    rotation: z.object({
        x: z.number(),
        y: z.number(),
        z: z.number(),
    }),
    shift: z.object({
        x: z.number(),
        y: z.number(),
    }),
    focalLength: z.number(),
    focusDistance: z.number(),
    fStop: z.number(),
    sensor: z.object({
        width: z.number(),
        height: z.number(),
    }),
    clipping: z.object({
        start: z.number(),
        end: z.number(),
    }),
    resolution: z.object({
        x: z.number(),
        y: z.number(),
    }),
    // the film exposure Blender renders with; an addon from before sends none
    exposure: z.optional(z.number()),
    // Blender's view transform, where the platform has it
    toneMapping: z.optional(blenderToneMappingSchema),
})

// the Blender scene's settings for the template's Render and Post Process nodes
const setRenderSettingsSchema = z.object({
    type: z.literal("set_render_settings"),
    ...inBatch,
    samples: z.number(),
    percentage: z.number(),
    toneMapping: z.optional(blenderToneMappingSchema),
})

const bytes = z.instanceof(Uint8Array)

// a typed array can only view bytes that start at a multiple of its element size
const float32s = (bytes: Uint8Array) =>
    bytes.byteOffset % 4 === 0 ? new Float32Array(bytes.buffer, bytes.byteOffset, bytes.byteLength / 4) : new Float32Array(bytes.slice().buffer)
const uint32s = (bytes: Uint8Array) =>
    bytes.byteOffset % 4 === 0 ? new Uint32Array(bytes.buffer, bytes.byteOffset, bytes.byteLength / 4) : new Uint32Array(bytes.slice().buffer)

const materialSlotsSchema = z.record(
    z.string(),
    z.object({
        name: z.nullable(z.string()),
        materialRevisionId: z.nullable(z.number()),
        offset: z.nullable(z.object({horizontal: z.number(), vertical: z.number(), rotation: z.number()})),
    }),
)

const addMeshSchema = z.object({
    type: z.literal("add_mesh"),
    name: z.string(),
    ...inBatch,
    consolidate: z.boolean(),
    convertToWorldSpaceUvs: z.boolean(),
    center: z.boolean(),
    location: z.object({
        x: z.number(),
        y: z.number(),
        z: z.number(),
    }),
    rotation: z.object({
        x: z.number(),
        y: z.number(),
        z: z.number(),
    }),
    polyVertexCount: bytes,
    vertices: bytes,
    vertexIndices: bytes,
    normals: bytes,
    normalIndices: bytes,
    uvs: z.array(bytes),
    uvIndices: z.array(bytes),
    materialIds: bytes,
    materialIdIndices: bytes,
    subdivisionLevel: z.number(),
    materialSlots: materialSlotsSchema,
    // objects sharing this mesh's data in Blender, which share its uploaded data objects too
    copies: z.optional(
        z.array(z.object({name: z.string(), location: vector3, rotation: vector3, collections: z.array(z.string()), materialSlots: materialSlotsSchema})),
    ),
})

const addCurveSchema = z.object({
    type: z.literal("add_curve"),
    name: z.string(),
    ...inBatch,
    location: z.object({
        x: z.number(),
        y: z.number(),
        z: z.number(),
    }),
    rotation: z.object({
        x: z.number(),
        y: z.number(),
        z: z.number(),
    }),
    vertexPositions: z.array(z.tuple([z.number(), z.number(), z.number()])),
    closed: z.optional(z.boolean()),
    // the object of a mesh from the same batch that the curve lies on; without it, the mesh selected in the editor
    meshObject: z.optional(z.nullable(z.string())),
})

// a Blender world as the HDRI light the platform shows it by; the rotation is Blender's world Mapping's, as the light keeps it
const addHdriLightSchema = z.object({
    type: z.literal("add_hdri_light"),
    name: z.string(),
    ...inBatch,
    hdriId: z.number(),
    rotation: z.tuple([z.number(), z.number(), z.number()]),
    mirror: z.boolean(),
    intensity: z.number(),
    saturation: z.number(),
})

// sent after the last object of a batch, also when Blender cancelled before sending them all
const endBatchSchema = z.object({
    type: z.literal("end_batch"),
    id: z.string(),
})

export type BlenderLightSource = SceneNodes.AreaLight | SceneNodes.PointLight | SceneNodes.SunLight | SceneNodes.LightPortal

export const blenderMessageSchema = z.discriminatedUnion("type", [
    addLightSchema,
    addPointLightSchema,
    addSunLightSchema,
    addLightPortalSchema,
    addCameraSchema,
    addMeshSchema,
    addCurveSchema,
    addHdriLightSchema,
    setRenderSettingsSchema,
    endBatchSchema,
])
export type BlenderMessage = z.infer<typeof blenderMessageSchema>
type MeshMessage = z.infer<typeof addMeshSchema>
type CurveMessage = z.infer<typeof addCurveSchema>
type RenderSettingsMessage = z.infer<typeof setRenderSettingsSchema>
type LightOrCameraMessage = Exclude<BlenderMessage, MeshMessage | CurveMessage | RenderSettingsMessage | z.infer<typeof endBatchSchema>>
type MaterialReferenceFor = ReturnType<typeof sharedMaterialReferences>["referenceFor"]

// an object from Blender ready to go into the template, at the group its collections lead to; named by its Blender object
type BlenderPlacement = {
    place: (at: (collections: string[]) => TemplateNodes, referenceFor: MaterialReferenceFor) => [string, TemplateNode][]
    placed?: () => void
}

type BlenderBatch = {
    operation: BackgroundOperationItem
    placements: Promise<BlenderPlacement>[]
    curves: CurveMessage[]
    render?: RenderSettingsMessage
    ready: number
}

// a mesh a batch brings is converted, and its data held, in the browser, so only a few at a time
const MESH_CONVERSIONS_AT_ONCE = 2
const MESH_SHOWN_TIMEOUT_MS = 30_000

const errorMessage = (error: unknown) => (error instanceof Error ? error.message : String(error))

function limitedTo(concurrent: number) {
    let running = 0
    const waiting: (() => void)[] = []
    return async <T>(task: () => Promise<T>): Promise<T> => {
        while (running >= concurrent) await new Promise<void>((resolve) => waiting.push(resolve))
        running++
        try {
            return await task()
        } finally {
            running--
            waiting.shift()?.()
        }
    }
}

const CONNECT_INTERVAL_MS = 3000
const BLENDER_CONNECTOR_SERVER_URL = "ws://127.0.0.1:17096"

@Injectable()
export class BlenderConnectorService implements OnDestroy {
    private readonly $_state = signal<"Disconnected" | "RetryConnecting" | "Connecting" | "Connected">("Disconnected")
    readonly $state = this.$_state.asReadonly()
    protected readonly $blenderConnector = signal<WebSocketMessaging | undefined>(undefined)
    private readonly destroyRef = inject(DestroyRef)
    private readonly logger: CmLogger = inject(CM_LOGGER_SCENE)
    private sceneManagerService = inject(SceneManagerService)
    private threeSceneManagerService: ThreeSceneManagerService | undefined = undefined
    private uploadService = inject(UploadGqlService)
    private readonly getMaterialLegacyGql = inject(GetMaterialLegacyIdForBlenderConnectorGQL)
    private readonly materialRevisionGql = inject(MaterialRevisionForBlenderGQL)
    private readonly textureRevisionGql = inject(TextureRevisionForBlenderGQL)
    private readonly textureSetRevisionGql = inject(TextureSetRevisionForBlenderGQL)
    private readonly dataObjectGql = inject(DataObjectForBlenderGQL)
    private readonly hdriGql = inject(HdriForBlenderGQL)
    private readonly exchangeNodesAndConnectionsGql = inject(ExchangeNodesAndConnectionsForBlenderGQL)
    private readonly sceneManagerCacheService = inject(SceneManagerCacheService)
    private readonly backgroundOperationService = inject(BackgroundOperationService)
    private readonly notifications = inject(NotificationsService)

    readonly $enable = signal<boolean>(false)
    private readonly batches = new Map<string, BlenderBatch>()
    // customers may take only their products to Blender, not their scenes or other templates; staff anything
    readonly $sendingAllowed = signal(false)
    private readonly meshConversions = limitedTo(MESH_CONVERSIONS_AT_ONCE)

    // a curve from Blender is laid onto the mesh selected here, which Blender shows before the upload
    private readonly $curveMesh = computed(() =>
        this.sceneManagerService
            .$selectedRootNodeParts()
            .map((part) => part.templateNode)
            .find(isMeshLikeNode),
    )

    constructor() {
        effect(() => {
            const blenderConnector = this.$blenderConnector()
            const curveMesh = this.$curveMesh()
            if (blenderConnector)
                blenderConnector.send({type: "curve_target", mesh: curveMesh ? (isNamedNode(curveMesh) ? curveMesh.parameters.name : "Mesh") : null})
        })
        effect(() => {
            if (this.$enable()) {
                this.$_state.set("Connecting")
            } else {
                this.$_state.set("Disconnected")
            }
        })
        effect(() => {
            const blenderConnector = untracked(() => this.$blenderConnector())

            switch (this.$_state()) {
                case "Connecting":
                    {
                        if (blenderConnector) {
                            this.$_state.set("Connected")
                        } else {
                            WebSocketMessaging.connect(BLENDER_CONNECTOR_SERVER_URL)
                                .pipe(takeUntilDestroyed(this.destroyRef))
                                .subscribe({
                                    next: (webSocketMessaging) => {
                                        this.$blenderConnector.set(webSocketMessaging)
                                        this.$_state.set("Connected")
                                        // Blender logs in on its own, so it has to know which platform the revision numbers it gets from here belong to
                                        webSocketMessaging.send({type: "environment", api: environment.gqlApiEndpoint})

                                        webSocketMessaging.message$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((message) => {
                                            this.onBlenderMessage(message)
                                        })
                                        webSocketMessaging.close$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
                                            this.$blenderConnector.set(undefined)
                                            if (this.$state() === "Connected") this.$_state.set("RetryConnecting")
                                        })
                                    },
                                    error: () => {
                                        if (this.$blenderConnector() === undefined && this.$state() === "Connecting") this.$_state.set("RetryConnecting")
                                    },
                                })
                        }
                    }
                    break
                case "RetryConnecting":
                    {
                        timer(CONNECT_INTERVAL_MS)
                            .pipe(takeUntilDestroyed(this.destroyRef))
                            .subscribe(() => {
                                if (this.$state() === "RetryConnecting") {
                                    this.$_state.set("Connecting")
                                }
                            })
                    }
                    break
                case "Disconnected":
                    {
                        if (blenderConnector) {
                            blenderConnector.destroy()
                            this.$blenderConnector.set(undefined)
                        }
                    }

                    break
            }
        })
    }

    ngOnDestroy(): void {
        const blenderConnector = this.$blenderConnector()

        if (blenderConnector) blenderConnector.destroy()
    }

    async sendToBlender({
        meshes: meshesToSend,
        meshCurves = [],
        lights = [],
        cameras = [],
        materialReferences = [],
        hdriLights = [],
        render = null,
    }: {
        meshes?: {nodes: SceneNodes.Mesh[]; includeMaterials: boolean}
        meshCurves?: SceneNodes.MeshCurveControl[]
        lights?: BlenderLightSource[]
        cameras?: SceneNodes.Camera[]
        // Blender sets these up without assigning them to anything
        materialReferences?: {materialId: number; materialRevisionId: number | null}[]
        hdriLights?: HdriLight[]
        render?: SceneNodes.RenderSettings | null
    }): Promise<void> {
        const blenderConnector = this.$blenderConnector()
        if (!blenderConnector) throw new Error("Not connected to Blender")
        if (!this.$sendingAllowed()) throw new Error("Only products can be sent to Blender")

        const meshes = meshesToSend?.nodes ?? []
        const platformMaterials = meshesToSend?.includeMaterials
            ? [...new Set(meshes.flatMap((mesh) => [...mesh.materialMap.values()]))].filter(
                  (material): material is IMaterialData => !!material && material.materialGraph.parameters.materialRevisionLegacyId != null,
              )
            : []
        const templateMaterialKeys = new Map(
            await Promise.all(platformMaterials.map(async (material) => [material, await this.templateMaterialKey(material)] as const)),
        )
        const templateMaterialKey = (material: IMaterialData) => templateMaterialKeys.get(material) ?? null
        const decals = new Map(
            meshes.flatMap((mesh) => {
                const node = getTemplateNodeForObjectPath(mesh.path)
                const material = mesh.materialMap.get(0)
                return node instanceof MeshDecal && material ? [[material, node] as const] : []
            }),
        )
        const materialsToSend = new Map(
            platformMaterials.map((material) => {
                const revisionLegacyId = material.materialGraph.parameters.materialRevisionLegacyId!
                const key = templateMaterialKey(material)
                const decal = decals.get(material)
                return [
                    key ?? `revision ${revisionLegacyId}`,
                    {
                        revisionLegacyId,
                        changedGraph: key === null ? null : material.materialGraph,
                        decal: decal ? {node: decal, alphaThreshold: material.alphaMaskThreshold ?? 0} : null,
                    },
                ] as const
            }),
        )
        const referencedRevisions = await Promise.all(materialReferences.map((reference) => this.referencedRevision(reference)))
        for (const revisionLegacyId of referencedRevisions) {
            const key = `revision ${revisionLegacyId}`
            if (!materialsToSend.has(key)) materialsToSend.set(key, {revisionLegacyId, changedGraph: null, decal: null})
        }

        let ready = 0
        const total = meshes.length + materialsToSend.size
        const sendStatus = (text: string | null) => {
            const message: BlenderStatusMessage = {type: "status", text}
            blenderConnector.send(message)
        }
        const counted = async <T>(preparing: Promise<T>) => {
            const prepared = await preparing
            sendStatus(`Template editor: preparing ${meshes.length} meshes and ${materialsToSend.size} materials, ${++ready} of ${total} ready`)
            return prepared
        }

        sendStatus(`Template editor: preparing ${meshes.length} meshes and ${materialsToSend.size} materials`)
        try {
            const curves = meshCurves.map((curve) => this.meshCurveForBlender(curve, meshes))
            const hdris = Promise.all(hdriLights.map((light) => this.hdriForBlender(light)))
            const [objects, materials] = await Promise.all([
                Promise.all(meshes.map((mesh) => counted(this.meshForBlender(mesh, templateMaterialKey)))),
                Promise.all(
                    [...materialsToSend.values()].map(({revisionLegacyId, changedGraph, decal}) =>
                        counted(this.materialForBlender(revisionLegacyId, changedGraph, decal)),
                    ),
                ),
            ])
            const message: BlenderImportObjectsMessage = {
                type: "import_objects",
                environment: environment.gqlApiEndpoint,
                objects: [
                    ...objects,
                    ...curves,
                    ...lights.map((light) => ({...this.namedForBlender(light.path, "Light"), ...blenderLight(light)})),
                    ...cameras.map((camera) => ({...this.namedForBlender(camera.path, "Camera"), ...blenderCamera(camera)})),
                ],
                materials,
                hdris: await hdris,
                render: render && {samples: render.samples, percentage: render.percentage},
                postProcess: cameras.length > 0 || render ? this.postProcessForBlender() : null,
            }
            blenderConnector.send(message)
        } catch (error) {
            sendStatus(null)
            throw error
        }
    }

    private postProcessForBlender(): BlenderPostProcess | null {
        const postProcess = this.sceneManagerService
            .$scene()
            .filter(SceneNodes.RenderPostProcessingSettings.is)
            .find((node) => node.templateDepth === 0)
        if (!postProcess) return null
        return {
            ev: Math.log2(postProcess.exposure),
            whiteBalance: postProcess.whiteBalance || null,
            toneMapping: postProcess.toneMapping ?? null,
            lutUrl: postProcess.lutUrl ?? null,
        }
    }

    private async hdriForBlender({parameters: {name, hdriId, rotation, mirror, intensity, saturation}}: HdriLight): Promise<BlenderHdri> {
        const {hdri} = await fetchThrowingErrors(this.hdriGql)({legacyId: hdriId})
        const hdriName = hdri.name ?? `HDRI ${hdriId}`
        if (!hdri.dataObject) throw new Error(`${hdriName} of the light ${name} has no file on the platform`)
        const {__typename, ...dataObject} = hdri.dataObject
        return {name, hdriLegacyId: hdri.legacyId, hdriName, dataObject, rotation: [...rotation], mirror, intensity, saturation}
    }

    private async referencedRevision({materialId, materialRevisionId}: {materialId: number; materialRevisionId: number | null}): Promise<number> {
        if (materialRevisionId !== null) return materialRevisionId
        const latest = await firstValueFrom(this.sceneManagerCacheService.materialGraphCache.evaluateMaterial(materialId, true))
        const {materialRevisionLegacyId} = latest.parameters
        if (materialRevisionLegacyId == null) throw new Error(`Material ${materialId} has no revision to send to Blender`)
        return materialRevisionLegacyId
    }

    // a template that changes nothing hands on the revision's own graph, as its material reference loads it; any change, however it
    // was computed, gives a graph of different content, which then keys the material
    private async templateMaterialKey(material: IMaterialData): Promise<string | null> {
        const revisionGraph = await firstValueFrom(
            this.sceneManagerCacheService.materialGraphCache.evaluateMaterialRevision(material.materialGraph.parameters.materialRevisionLegacyId!, true),
        )
        return revisionGraph.uniqueId === material.materialGraph.uniqueId ? null : material.materialGraph.uniqueId
    }

    // fetched with this session, so the addon needs no login and gets them from the environment the template came from
    private async materialForBlender(
        revisionLegacyId: number,
        changedGraph: MaterialGraph | null,
        decalSource: {node: MeshDecal; alphaThreshold: number} | null,
    ): Promise<BlenderMaterial> {
        const {materialRevision} = await fetchThrowingErrors(this.materialRevisionGql)({legacyId: revisionLegacyId})
        // Blender builds a decal's mask and colour overlay on the revision's own graph
        const decal = changedGraph && decalSource ? await this.decalForBlender(revisionLegacyId, changedGraph, decalSource) : null
        const templateChange = changedGraph && !decal ? await this.templateChange(revisionLegacyId, changedGraph) : null
        const exchangeNodesAndConnections = templateChange
            ? (await fetchThrowingErrors(this.exchangeNodesAndConnectionsGql)({graphNode: templateChange.graph.serialize()}))
                  .exchangeNodesAndConnectionsForGraphNode
            : materialRevision.exchangeNodesAndConnections
        const {nodes} = exchangeNodesAndConnections
        const textureRevisionIds = new Set(nodes.flatMap((node) => node.textureRevisionId ?? []))
        const textureSetRevisionIds = new Set(nodes.flatMap((node) => node.textureSetRevisionId ?? []))
        const [textureRevisions, textureSetRevisions] = await Promise.all([
            Promise.all([...textureRevisionIds].map(async (id) => (await fetchThrowingErrors(this.textureRevisionGql)({id})).textureRevision)),
            Promise.all([...textureSetRevisionIds].map(async (id) => (await fetchThrowingErrors(this.textureSetRevisionGql)({id})).textureSetRevision)),
        ])
        return {
            revisionLegacyId,
            templateMaterialKey: changedGraph?.uniqueId ?? null,
            templateOffset: templateChange?.offset ?? null,
            decal,
            materialRevision: {...materialRevision, exchangeNodesAndConnections},
            textureRevisions,
            textureSetRevisions,
        }
    }

    private async decalForBlender(
        revisionLegacyId: number,
        decalGraph: MaterialGraph,
        {node, alphaThreshold}: {node: MeshDecal; alphaThreshold: number},
    ): Promise<BlenderDecal> {
        const [mask, colorOverlay] = await Promise.all([node.parameters.mask, node.parameters.color].map((image) => this.decalImage(node, image)))
        const {invertMask} = node.parameters
        const revisionGraph = await firstValueFrom(this.sceneManagerCacheService.materialGraphCache.evaluateMaterialRevision(revisionLegacyId, true))
        const {graph, widthCm, heightCm} = rebuildDecal(revisionGraph, decalGraph, {mask, colorOverlay, invertMask})
        if (graph.uniqueId !== decalGraph.uniqueId) {
            const changes = materialGraphChanges(graph, decalGraph).join("; ")
            throw new Error(`Decal "${node.parameters.name}" changes material revision ${revisionLegacyId} in more ways than a decal does: ${changes}`)
        }
        return {widthCm, heightCm, mask, invertMask, colorOverlay, alphaThreshold}
    }

    private async decalImage(decal: MeshDecal, image: MeshDecal["parameters"]["mask"]): Promise<BlenderDecalImage | null> {
        if (!image) return null
        if (!(image instanceof DataObjectReference)) {
            throw new Error(`Decal "${decal.parameters.name}" uses an image made in the template, and only uploaded images can be sent to Blender`)
        }
        const {__typename, ...dataObject} = (await fetchThrowingErrors(this.dataObjectGql)({legacyId: image.parameters.dataObjectId})).dataObject
        return {...dataObject, imageColorSpace: dataObject.imageColorSpace ?? null}
    }

    // Blender builds texture nodes itself, from the revision's graph before flattening, so the template's change has to be made there
    private async templateChange(revisionLegacyId: number, changedGraph: MaterialGraph) {
        const {materialGraphCache} = this.sceneManagerCacheService
        const revisionGraph = await firstValueFrom(materialGraphCache.evaluateMaterialRevision(revisionLegacyId, true))
        const offsets = uvOffsetsBetween(revisionGraph, changedGraph)
        if (!offsets) {
            const {materialLegacyId, name} = changedGraph.parameters
            const material = `material ${materialLegacyId ?? "?"}${name ? ` (${name})` : ""}`
            const changes = materialGraphChanges(revisionGraph, changedGraph).join("; ")
            throw new Error(`The template changes revision ${revisionLegacyId} of ${material} in a way that cannot be sent to Blender: ${changes}`)
        }
        return {
            graph: offsets.reduce(transformOffsetUvs, await firstValueFrom(materialGraphCache.evaluateMaterialRevision(revisionLegacyId, false))),
            offset: combineUvOffsets(offsets),
        }
    }

    private async meshForBlender(mesh: SceneNodes.Mesh, templateMaterialKey: (material: IMaterialData) => string | null): Promise<BlenderMeshObject> {
        const source = blenderMeshSource(mesh.completeMeshData.abstract)
        const resources = await this.sceneManagerService.loadEvaluationResources(source.dataObjectReferences)
        const polyMesh = toBlenderCoordinates(await firstValueFrom(evaluateRawPolyMesh(this.sceneManagerService.workerService, source.graph, resources)))
        return {
            type: "mesh",
            ...this.namedForBlender(mesh.path, "Mesh"),
            matrixWorld: toBlenderTransform(mesh.transform, false),
            polyVertexCount: polyMesh.polyVertexCount,
            vertices: polyMesh.position.values,
            vertexIndices: polyMesh.position.indices,
            normals: polyMesh.normal.values,
            normalIndices: polyMesh.normal.indices,
            uvs: polyMesh.uvs.map((uv) => uv.values),
            uvIndices: polyMesh.uvs.map((uv) => uv.indices),
            materialIds: polyMesh.materialID.values,
            materialIdIndices: polyMesh.materialID.indices,
            subdivision: {viewportLevels: source.viewportLevels, renderLevels: source.renderLevels},
            materialSlots: blenderMaterialSlots(mesh.materialMap, templateMaterialKey),
            decal: mesh.isDecal,
        }
    }

    // the conversion and the upload take long for a large mesh, so the mesh shows in the operations list from the moment it arrives
    private async addMeshFromBlender(message: MeshMessage) {
        const {name} = message
        const operation = this.backgroundOperationService.addBackgroundOperationToList("Blender", name, false)
        const step = (description: string) => {
            operation.description = `${name}: ${description}`
            operation.progressSubject.next(-1)
        }
        try {
            const placement = await this.prepareMeshFromBlender(message, step, true)
            this.sceneManagerService.modifyTemplateGraph((graph) => {
                const references = sharedMaterialReferences(graph)
                placement.place(() => graph.parameters.nodes, references.referenceFor)
                references.appendNew()
            })
            placement.placed?.()
            operation.description = name
            operation.progressSubject.complete()
        } catch (error) {
            this.logger.error(`Adding mesh ${name} from Blender failed:`, error)
            operation.errorCause = errorMessage(error)
            operation.progressSubject.error(error)
            this.notifications.showError(error)
        }
    }

    private async prepareMeshFromBlender(message: MeshMessage, step: (description: string) => void, showUploadToolbar: boolean): Promise<BlenderPlacement> {
        const {
            name,
            convertToWorldSpaceUvs,
            center,
            consolidate,
            polyVertexCount,
            vertices,
            vertexIndices,
            normals,
            normalIndices,
            uvs,
            uvIndices,
            materialIds,
            materialIdIndices,
            subdivisionLevel,
        } = message
        const defaultCustomerId = this.sceneManagerService.$defaultCustomerId()
        if (!defaultCustomerId) throw new Error("defaultCustomerId is not defined")

        step("converting")
        const rawPolyMeshData: RawPolyMeshData = fromBlenderCoordinates({
            polyVertexCount: uint32s(polyVertexCount),
            position: {elementType: "float32", elementCount: 3, values: float32s(vertices), indices: uint32s(vertexIndices), consolidate: false},
            normal: {elementType: "float32", elementCount: 3, values: float32s(normals), indices: uint32s(normalIndices), consolidate: false},
            materialID: {elementType: "uint32", elementCount: 1, values: uint32s(materialIds), indices: uint32s(materialIdIndices), consolidate: false},
            uvs: uvs.map((uv, index) => ({
                elementType: "float32",
                elementCount: 2,
                values: float32s(uv),
                indices: uint32s(uvIndices[index]),
                consolidate: false,
            })),
        })
        const options: MeshConversionOptions = {consolidate, resolution: 0.001, center, convertToWorldSpaceUvs}
        const objects = [{...message, collections: message.collections ?? []}, ...(message.copies ?? [])]
        const [result, slotMaterials] = await Promise.all([
            firstValueFrom(convertRawPolyMeshToDracoAndPLY(this.sceneManagerService.workerService, rawPolyMeshData, options)),
            Promise.all(objects.map((object) => this.slotMaterialsFromBlender(object.materialSlots))),
        ])
        const {drcData, plyData} = result

        step("uploading")
        const upload = (data: Uint8Array, extension: string, type: string) =>
            this.uploadService.createAndUploadDataObject(
                new File([data as Uint8Array<ArrayBuffer>], filenamify(`${name}.${extension}`, {replacement: "_"}), {type}),
                {type: DataObjectType.Mesh, organizationLegacyId: defaultCustomerId},
                {processUpload: true, showUploadToolbar},
            )
        const [plyDataObject, dracoDataObject] = await Promise.all([upload(plyData, "ply", "application/ply"), upload(drcData, "drc", "application/draco")])

        const storedMesh = (object: (typeof objects)[number], slots: (typeof slotMaterials)[number], referenceFor: MaterialReferenceFor) =>
            new StoredMesh({
                parameters: {
                    name: object.name,
                    metaData: {
                        dracoBitDepth: result.dracoBitDepth,
                        dracoResolution: result.dracoResolution,
                        defaultPosition: result.defaultPosition,
                        centered: result.centered,
                        exporterVersion: result.exporterVersion,
                    },
                    drcDataObject: new DataObjectReference({parameters: {name: `${name}.drc`, dataObjectId: dracoDataObject.legacyId}}),
                    plyDataObject: new DataObjectReference({parameters: {name: `${name}.ply`, dataObjectId: plyDataObject.legacyId}}),
                    subdivisionRenderIterations: subdivisionLevel,
                    materialAssignments: new MaterialAssignments({
                        parameters: Object.fromEntries(
                            Object.entries(slots).map(([key, slot]) => [
                                key,
                                slot &&
                                    new MaterialAssignment({
                                        parameters: {
                                            node: referenceFor(slot.material),
                                            side: "front",
                                            ...(slot.offset
                                                ? {
                                                      horizontalOffset: slot.offset.horizontal,
                                                      verticalOffset: slot.offset.vertical,
                                                      rotation: slot.offset.rotation,
                                                  }
                                                : {}),
                                        },
                                    }),
                            ]),
                        ),
                    }),
                    materialSlotNames: Object.fromEntries(
                        Object.entries(object.materialSlots).flatMap(([key, {name: slotName}]) => (slotName === null ? [] : [[key, slotName]])),
                    ),
                    lockedTransform: fromBlenderTransform(object.location, object.rotation, false)
                        .multiply(Matrix4.translation(...result.defaultPosition))
                        .toArray(),
                    visible: true,
                    visibleDirectly: true,
                    visibleInReflections: true,
                    visibleInRefractions: true,
                    castRealtimeShadows: true,
                    receiveRealtimeShadows: true,
                },
            })

        return {
            place: (at, referenceFor) =>
                objects.map((object, index) => {
                    const node = storedMesh(object, slotMaterials[index], referenceFor)
                    at(object.collections).addEntry(node)
                    return [object.name, node] as [string, TemplateNode]
                }),
            placed: () => {
                for (const slot of slotMaterials.flatMap((slots) => Object.values(slots))) {
                    if (slot?.material.materialRevisionId === null)
                        this.sceneManagerService.reloadLatestMaterialRevision(slot.material.materialId, slot.revisionLegacyId)
                }
            },
        }
    }

    // a slot holding its material's latest revision follows the material from then on, as a reference the editor adds does
    private async slotMaterialsFromBlender(materialSlots: z.infer<typeof materialSlotsSchema>) {
        const entries = await Promise.all(
            Object.entries(materialSlots).map(async ([key, {materialRevisionId, offset}]) => {
                if (materialRevisionId === null) return [key, null] as const
                const {material} = (await fetchThrowingErrors(this.getMaterialLegacyGql)({legacyId: materialRevisionId})).materialRevision
                const latest = material.latestCyclesRevision?.legacyId === materialRevisionId
                const reference = {
                    materialId: material.legacyId,
                    materialRevisionId: latest ? null : materialRevisionId,
                    name: material.name ?? `Material ${material.legacyId}`,
                }
                return [key, {material: reference, revisionLegacyId: materialRevisionId, offset}] as const
            }),
        )
        return entries.length > 0 ? Object.fromEntries(entries) : {"0": null}
    }

    private namedForBlender(path: SceneNodes.SceneNode["path"], fallback: string) {
        const node = getTemplateNodeForObjectPath(path)
        return {name: isNamedNode(node) ? node.parameters.name : fallback, collections: blenderCollections(path)}
    }

    private meshCurveForBlender(curve: SceneNodes.MeshCurveControl, meshes: SceneNodes.Mesh[]): BlenderCurveObject {
        if (!this.threeSceneManagerService) throw new Error("ThreeSceneManagerService is not set")
        const projection = projectMeshCurveOntoMesh(this.threeSceneManagerService, curve, toThreeMatrix(curve.transform))
        const hitPoints = (projection?.hitPoints ?? []).map(({x, y, z}) => new Vector3(x, y, z))
        const meshIndex = meshes.findIndex((mesh) => mesh.id === curve.mesh.id)
        return {
            type: "curve",
            ...this.namedForBlender(curve.path, "Curve"),
            matrixWorld: toBlenderTransform(curve.transform, true),
            splines: blenderCurveSplines(
                {hitPoints, valid: projection?.valid ?? [], segments: curve.completeCurveData.reified?.segments ?? []},
                curve.controlPoints,
                curve.closed,
            ),
            meshIndex: meshIndex === -1 ? null : meshIndex,
        }
    }

    setThreeSceneManagerService(threeSceneManagerService: ThreeSceneManagerService | undefined) {
        this.threeSceneManagerService = threeSceneManagerService
    }

    private onBlenderMessage(message: TraverseType) {
        const parseResult = blenderMessageSchema.safeParse(message)
        if (!parseResult.success) {
            throw new Error("Invalid message from Blender")
        }

        const {data} = parseResult
        this.processBlenderMessage(data)
    }

    private processBlenderMessage(message: BlenderMessage) {
        if (message.type === "end_batch") {
            void this.finishBatch(message.id)
            return
        }
        if (message.batch) {
            this.addToBatch(message.batch.id, message)
            return
        }
        switch (message.type) {
            case "add_mesh":
                void this.addMeshFromBlender(message)
                break
            case "set_render_settings": {
                let problem: string | null = null
                this.sceneManagerService.modifyTemplateGraph((graph) => (problem = this.renderSettingsFromBlender(graph.parameters.nodes, message)))
                if (problem) this.notifications.showErrorString(problem)
                break
            }
            case "add_curve": {
                const selectedMesh = this.$curveMesh()
                if (!selectedMesh) {
                    this.notifications.showErrorString(`Select the mesh the curve ${message.name} from Blender lies on`)
                    return
                }
                try {
                    const curve = this.curveNodeFromBlender(message, selectedMesh)
                    if (curve) this.sceneManagerService.modifyTemplateGraph((graph) => graph.parameters.nodes.addEntry(curve))
                } catch (error) {
                    this.notifications.showError(error)
                }
                break
            }
            default: {
                const node = this.lightOrCameraNodeFromBlender(message)
                this.sceneManagerService.modifyTemplateGraph((graph) => graph.parameters.nodes.addEntry(node))
            }
        }
    }

    private lightOrCameraNodeFromBlender(message: LightOrCameraMessage): TemplateNode {
        switch (message.type) {
            case "add_light": {
                const {name, location, rotation, color, width, height, spread, power, visible} = message
                const transform = fromBlenderTransform(location, rotation, true)
                const target = targetFromTransform(transform, 100)
                return new AreaLight({
                    parameters: {
                        name,
                        width: fromBlenderLength(width),
                        height: fromBlenderLength(height),
                        lockedTransform: transform.toArray(),
                        visible: true,
                        target: target.toArray(),
                        targeted: false,
                        intensity: power,
                        color: [color.r, color.g, color.b],
                        on: true,
                        directionality: 1 - spread / Math.PI,
                        visibleDirectly: visible,
                        visibleInReflections: true,
                        visibleInRefractions: true,
                        // a light in Blender never stops the rays that meet it
                        transparent: true,
                        lightType: "Blender",
                    },
                })
            }
            case "add_point_light": {
                const {name, location, rotation, color, radius, power, visible} = message
                const transform = fromBlenderTransform(location, rotation, false)
                return new PointLight({
                    parameters: {
                        name,
                        radius: fromBlenderLength(radius),
                        lockedTransform: transform.toArray(),
                        visible: true,
                        intensity: power,
                        color: [color.r, color.g, color.b],
                        on: true,
                        visibleDirectly: visible,
                        visibleInReflections: true,
                        visibleInRefractions: true,
                        lightType: "Blender",
                    },
                })
            }
            case "add_sun_light": {
                const {name, location, rotation, color, angle, power, visible} = message
                const transform = fromBlenderTransform(location, rotation, true)
                return new SunLight({
                    parameters: {
                        name,
                        angle: (angle * 180) / Math.PI,
                        lockedTransform: transform.toArray(),
                        visible: true,
                        intensity: power,
                        color: [color.r, color.g, color.b],
                        on: true,
                        visibleDirectly: visible,
                        visibleInReflections: true,
                        visibleInRefractions: true,
                        lightType: "Blender",
                    },
                })
            }
            case "add_light_portal": {
                const {name, location, rotation, width, height} = message
                return new LightPortal({
                    parameters: {
                        name,
                        width: fromBlenderLength(width),
                        height: fromBlenderLength(height),
                        lockedTransform: fromBlenderTransform(location, rotation, true).toArray(),
                        visible: true,
                    },
                })
            }
            case "add_hdri_light": {
                const {name, hdriId, rotation, mirror, intensity, saturation} = message
                return new HdriLight({parameters: {name, hdriId, rotation, mirror, intensity, saturation}})
            }
            case "add_camera": {
                const {
                    name,
                    location,
                    rotation,
                    shift,
                    focalLength,
                    focusDistance,
                    fStop,
                    sensor,
                    clipping,
                    resolution,
                    exposure = 1,
                    toneMapping = "linear",
                } = message
                const transform = fromBlenderTransform(location, rotation, true)
                const focalDistance = fromBlenderLength(focusDistance)
                const target = targetFromTransform(transform, focalDistance)
                return new Camera({
                    parameters: {
                        name,
                        target: target.toArray(),
                        targeted: false,
                        lockedTransform: transform.toArray(),
                        visible: true,
                        resolutionX: resolution.x,
                        resolutionY: resolution.y,
                        filmGauge: sensor.width,
                        fStop: fStop,
                        focalLength,
                        focalDistance,
                        autoFocus: false,
                        ev: Math.log2(exposure),
                        shiftX: shift.x,
                        shiftY: shift.y,
                        automaticVerticalTilt: false,
                        enableOrbiting: true,
                        enablePanning: true,
                        screenSpacePanning: true,
                        adaptAspectRatio: true,
                        nearClip: fromBlenderLength(clipping.start),
                        farClip: fromBlenderLength(clipping.end),
                        toneMapping: defaultsForToneMapping(toneMapping),
                        enableDamping: false,
                        dampingFactor: 0.05,
                    },
                })
            }
        }
    }

    // laid onto the mesh as the editor shows it, which the curve's points from Blender only come close to; null when none meets it
    private curveNodeFromBlender(message: CurveMessage, mesh: MeshLikeNode): MeshCurve | null {
        const {name, location, rotation, vertexPositions, closed = false} = message
        if (!this.threeSceneManagerService) throw new Error("ThreeSceneManagerService is not set")
        const meshNode = this.shownMesh(mesh)
        const threeObject = meshNode && this.threeSceneManagerService.getThreeObject(meshNode.id)
        const meshTransform = this.sceneManagerService.getTransformAccessor(mesh)?.getTransform()
        if (!threeObject || !meshTransform) throw new Error(`The curve ${name} from Blender cannot be laid onto its mesh until the mesh is shown`)

        const inverseMeshTransform = meshTransform.inverse()
        const curveTransform = fromBlenderTransform(location, rotation, true)
        const localPositions = simplifyControlPoints(
            densifyControlPoints(
                removeDuplicates(
                    vertexPositions.map((x) => {
                        const position = curveTransform.multiplyVectorXYZW(...Vector3.fromArray(x.map(fromBlenderLength)).toArray(), 1)
                        const localPosition = inverseMeshTransform.multiplyVectorXYZW(...position)
                        return new Vector3(localPosition[0], localPosition[1], localPosition[2])
                    }),
                ),
                0.03,
            ),
            0.03,
        )

        const segmentLength = 0.1
        const queryRange = 2 * segmentLength
        const mappedPositions = closestCurvePointsToObject(
            new Float32Array(localPositions.map((v) => v.toArray()).flat()),
            toThreeMatrix(meshTransform),
            queryRange,
            threeObject.getRenderObject(),
        )

        const controlPoints: PositionControlPoint[] = []
        for (let i = 0; i < mappedPositions.hitPoints.length; i++) {
            const hitPoint = mappedPositions.hitPoints[i]
            const normal = mappedPositions.hitNormals[i]
            if (mappedPositions.valid[i]) {
                controlPoints.push({
                    position: [hitPoint.x, hitPoint.y, hitPoint.z],
                    normal: [normal.x, normal.y, normal.z],
                    corner: false,
                    sectionBreak: false,
                })
            }
        }
        if (controlPoints.length === 0) return null
        return new MeshCurve({parameters: {name, mesh, closed, controlPoints, visible: true}})
    }

    private shownMesh(mesh: MeshLikeNode) {
        return this.sceneManagerService
            .$scene()
            .filter(SceneNodes.Mesh.is)
            .find((x) => getRootTemplateNodeForObjectPath(x.path) === mesh)
    }

    private async meshShown(mesh: MeshLikeNode): Promise<void> {
        const deadline = Date.now() + MESH_SHOWN_TIMEOUT_MS
        while (Date.now() < deadline) {
            const meshNode = this.shownMesh(mesh)
            if (meshNode && this.threeSceneManagerService?.getThreeObject(meshNode.id)) return
            await firstValueFrom(timer(200))
        }
    }

    private addToBatch(id: string, message: Exclude<BlenderMessage, {type: "end_batch"}>) {
        let batch = this.batches.get(id)
        if (!batch) {
            batch = {
                operation: this.backgroundOperationService.addBackgroundOperationToList("Blender", "Objects from Blender", false),
                placements: [],
                curves: [],
                ready: 0,
            }
            this.batches.set(id, batch)
        }
        const current = batch
        if (message.type === "add_curve") {
            current.curves.push(message)
            return
        }
        if (message.type === "set_render_settings") {
            current.render = message
            return
        }
        const preparing =
            message.type === "add_mesh"
                ? this.meshConversions(() => this.prepareMeshFromBlender(message, () => {}, false))
                : Promise.resolve().then(() => this.placementOf(this.lightOrCameraNodeFromBlender(message), message.collections ?? []))
        const prepared = preparing.catch((error: unknown) => {
            this.logger.error(`Preparing ${message.name} from Blender failed:`, error)
            throw new Error(`${message.name}: ${errorMessage(error)}`)
        })
        current.placements.push(prepared)
        const showProgress = () => {
            current.operation.description = `Objects from Blender: ${current.ready} of ${current.placements.length} ready`
            current.operation.progressSubject.next((100 * current.ready) / current.placements.length)
        }
        showProgress()
        prepared.then(
            () => {
                current.ready++
                showProgress()
            },
            () => {},
        )
    }

    // everything the batch brought goes into the template in one change, which one undo takes back
    private async finishBatch(id: string) {
        const batch = this.batches.get(id)
        if (!batch) return
        this.batches.delete(id)
        const {operation} = batch
        const results = await Promise.allSettled(batch.placements)
        const problems = results.flatMap((result) => (result.status === "rejected" ? [errorMessage(result.reason)] : []))
        const ready = results.flatMap((result) => (result.status === "fulfilled" ? [result.value] : []))

        const placed = new Map<string, TemplateNode>()
        const {render} = batch
        if (ready.length > 0 || render) {
            this.sceneManagerService.modifyTemplateGraph((graph) => {
                const references = sharedMaterialReferences(graph)
                const at = (collections: string[]) => nodesAlongCollections(graph, collections)
                for (const placement of ready) for (const [name, node] of placement.place(at, references.referenceFor)) placed.set(name, node)
                references.appendNew()
                const renderProblem = render && this.renderSettingsFromBlender(graph.parameters.nodes, render)
                if (renderProblem) problems.push(renderProblem)
            })
            for (const placement of ready) placement.placed?.()
        }
        const curveProblems = await this.addCurvesOfBatch(batch.curves, placed)
        problems.push(...curveProblems)

        operation.description = `${placed.size + batch.curves.length - curveProblems.length} objects from Blender`
        if (problems.length > 0) {
            operation.errorCause = problems.join("; ")
            this.notifications.showErrorString(`Not everything from Blender could be added: ${problems.join("; ")}`)
        }
        if (placed.size === 0 && problems.length > 0) operation.progressSubject.error(new Error(operation.errorCause))
        else operation.progressSubject.complete()
    }

    private async addCurvesOfBatch(curves: CurveMessage[], placed: Map<string, TemplateNode>): Promise<string[]> {
        const problems: string[] = []
        const added: [string[], MeshCurve][] = []
        for (const curve of curves) {
            const placedMesh = curve.meshObject ? placed.get(curve.meshObject) : undefined
            const mesh = placedMesh && isMeshLikeNode(placedMesh) ? placedMesh : this.$curveMesh()
            if (!mesh) {
                problems.push(`${curve.name}: select the mesh it lies on in the template editor`)
                continue
            }
            try {
                await this.meshShown(mesh)
                const node = this.curveNodeFromBlender(curve, mesh)
                if (node) added.push([curve.collections ?? [], node])
                else problems.push(`${curve.name}: it does not lie on its mesh`)
            } catch (error) {
                problems.push(`${curve.name}: ${errorMessage(error)}`)
            }
        }
        if (added.length > 0) {
            this.sceneManagerService.modifyTemplateGraph((graph) => {
                for (const [collections, node] of added) nodesAlongCollections(graph, collections).addEntry(node)
            })
        }
        return problems
    }

    // a template has one Render node for Blender's settings to go to, or gets one; with several, which one is meant is the artist's call
    private renderSettingsFromBlender(nodes: TemplateNodes, {samples, percentage, toneMapping}: RenderSettingsMessage): string | null {
        const postProcessProblem = this.postProcessFromBlender(nodes, toneMapping)
        const renderProblem = this.renderNodeFromBlender(nodes, samples, percentage)
        return [renderProblem, postProcessProblem].filter((problem) => problem !== null).join("; ") || null
    }

    private rootNodesOf<T extends TemplateNode>(type: (node: SceneNodes.SceneNode) => boolean, nodeClass: abstract new (...args: never[]) => T): T[] {
        return [
            ...new Set(
                this.sceneManagerService
                    .$scene()
                    .filter((node) => type(node) && "templateDepth" in node && node.templateDepth === 0)
                    .map((node) => getTemplateNodeForObjectPath(node.path))
                    .filter((node): node is T => node instanceof nodeClass),
            ),
        ]
    }

    // a template without a Post Process node gets one, with Linear where Blender's view has no platform counterpart, as it rendered without
    private postProcessFromBlender(nodes: TemplateNodes, mode: z.infer<typeof blenderToneMappingSchema> | undefined): string | null {
        const postProcesses = this.rootNodesOf(SceneNodes.RenderPostProcessingSettings.is, PostProcessRender)
        if (postProcesses.length > 1) return `Tone mapping: the template has ${postProcesses.length} Post Process nodes, so they stay as they are`
        const [postProcess] = postProcesses
        if (postProcess) {
            if (mode && postProcess.parameters.toneMapping.mode !== mode) postProcess.updateParameters({toneMapping: defaultsForToneMapping(mode)})
        } else {
            nodes.addEntry(
                new PostProcessRender({
                    parameters: {
                        mode: "whiteBackground",
                        ev: 0,
                        whiteBalance: 0,
                        transparent: false,
                        composite: false,
                        backgroundColor: [1, 1, 1],
                        processShadows: false,
                        shadowInner: 0,
                        shadowOuter: 0,
                        shadowFalloff: 1,
                        shadowOpacity: 1,
                        autoCrop: false,
                        autoCropMargin: 50,
                        toneMapping: defaultsForToneMapping(mode ?? "linear"),
                    },
                }),
            )
        }
        return null
    }

    private renderNodeFromBlender(nodes: TemplateNodes, samples: number, percentage: number): string | null {
        const renders = this.rootNodesOf(SceneNodes.RenderSettings.is, Render)
        if (renders.length > 1) return `Render settings: the template has ${renders.length} Render nodes, so they stay as they are`
        const [render] = renders
        if (!render) {
            nodes.addEntry(new Render({parameters: {samples, percentage, textureStreaming: false, renderTiling: "auto", denoiseTiling: "auto"}}))
            return null
        }
        const linked = (["samples", "percentage"] as const).filter((key) => typeof render.parameters[key] !== "number")
        if (linked.length > 0) return `Render settings: the Render node takes its ${linked.join(" and ")} from another node, so it stays as it is`
        render.updateParameters({samples, percentage})
        return null
    }

    private placementOf(node: TemplateNode, collections: string[]): BlenderPlacement {
        return {
            place: (at) => {
                at(collections).addEntry(node)
                return [[isNamedNode(node) ? node.parameters.name : "", node]]
            },
        }
    }
}
